import { Grid, Box, Typography, Button, CircularProgress } from "@mui/material";
import SectionCard from "./SectionCard";
import React, { useEffect, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";
import pdfjsWorker from "pdfjs-dist/legacy/build/pdf.worker.entry";
import CoBC_Policy_Modenik from "../../../../assets/CoBC_Policy_Modenik.pdf";
import useScrolledToBottom from "./useScrolledToBottom";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

const loadPdfDocument = async () => {
  try {
    return await pdfjsLib.getDocument(CoBC_Policy_Modenik).promise;
  } catch {
    pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;
    const response = await fetch(CoBC_Policy_Modenik);
    if (!response.ok) {
      throw new Error(`Failed to fetch PDF (${response.status})`);
    }
    const data = await response.arrayBuffer();
    return pdfjsLib.getDocument({ data }).promise;
  }
};

const isCOBCVerified = (termsAgreedDateTime) =>
  termsAgreedDateTime != null && String(termsAgreedDateTime).trim() !== "";

const formatSignedDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
};

const Step7COBC = ({ formData, groupId, setFieldValue }) => {
  const verified = isCOBCVerified(formData.termsAgreedDateTime);
  const isAgreed = Boolean(formData.cobcAccepted);
  const skipScrollGate = verified || isAgreed || groupId?.isAdmin;

  const [pdfLoading, setPdfLoading] = useState(true);
  const [pdfError, setPdfError] = useState("");
  const [pagesRendered, setPagesRendered] = useState(false);
  const pdfPagesRef = useRef(null);
  const contentReady = pagesRendered && !pdfLoading && !pdfError;
  const { scrollRef, scrolledToBottom, checkScroll } =
    useScrolledToBottom(contentReady);

  useEffect(() => {
    let cancelled = false;

    const renderPdfPages = async () => {
      setPdfLoading(true);
      setPdfError("");
      setPagesRendered(false);

      const container = pdfPagesRef.current;
      if (container) {
        container.innerHTML = "";
      }

      try {
        const pdf = await loadPdfDocument();
        if (cancelled || !pdfPagesRef.current) return;

        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum += 1) {
          const page = await pdf.getPage(pageNum);
          if (cancelled || !pdfPagesRef.current) return;

          const viewport = page.getViewport({ scale: 1.25 });
          const canvas = document.createElement("canvas");
          const context = canvas.getContext("2d");
          canvas.height = viewport.height;
          canvas.width = viewport.width;
          canvas.style.display = "block";
          canvas.style.width = "100%";
          canvas.style.height = "auto";
          canvas.style.marginBottom = pageNum < pdf.numPages ? "12px" : "0";

          await page.render({ canvasContext: context, viewport }).promise;
          pdfPagesRef.current.appendChild(canvas);
        }

        if (!cancelled) {
          setPagesRendered(true);
        }
      } catch {
        if (!cancelled) {
          setPdfError(
            "Unable to load COBC document. Please refresh the page and try again.",
          );
        }
      } finally {
        if (!cancelled) {
          setPdfLoading(false);
          requestAnimationFrame(() => {
            requestAnimationFrame(() => checkScroll());
          });
        }
      }
    };

    renderPdfPages();

    return () => {
      cancelled = true;
    };
  }, [checkScroll]);

  const canAgree = skipScrollGate || scrolledToBottom;

  return (
    <>
      <SectionCard title="VENDOR COBC">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box
              ref={scrollRef}
              sx={{
                maxHeight: 400,
                overflowY: "auto",
                p: 1,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
              }}
            >
              {pdfLoading && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress size={32} />
                </Box>
              )}
              {pdfError && (
                <Typography sx={{ fontSize: "14px", color: "error.main" }}>
                  {pdfError}
                </Typography>
              )}
              <Box ref={pdfPagesRef} />
            </Box>
          </Grid>

          {!skipScrollGate && !pdfLoading && !pdfError && !scrolledToBottom && (
            <Grid item xs={12}>
              <Typography sx={{ fontSize: "14px", color: "warning.main" }}>
                Please scroll through the entire COBC document to the last page
                before proceeding.
              </Typography>
            </Grid>
          )}

          {verified && (
            <Grid item xs={12}>
              <Typography sx={{ fontSize: "15px", color: "success.main" }}>
                You have accepted the COBC.
              </Typography>
            </Grid>
          )}
        </Grid>
      </SectionCard>

      {!isAgreed && !groupId?.isAdmin && (
        <Button
          variant="contained"
          color="primary"
          type="button"
          disabled={!canAgree || pdfLoading || Boolean(pdfError)}
          onClick={() => setFieldValue("cobcAccepted", true)}
          sx={{ mr: 1, mt: 2 }}
        >
          I AGREE
        </Button>
      )}

      {!formData.termsAgreedDateTime?.length > 0 && isAgreed && !groupId?.isAdmin && (
        <Typography sx={{ color: "success.main", mt: 2 }}>
          COBC accepted for this step. You can proceed to next step.
        </Typography>
      )}

      {groupId?.isAdmin &&
        (formData.termsAgreedDateTime?.length > 0 ? (
          <Typography sx={{ color: "success.main", mt: 2, ml: 2 }}>
            Digitally signed on: {formatSignedDate(formData.termsAgreedDateTime)}
          </Typography>
        ) : (
          <Typography sx={{ color: "error.main", mt: 2, ml: 2 }}>
            Agreement not signed yet
          </Typography>
        ))}
    </>
  );
};

export default Step7COBC;
